// Shared helper for opening PDFs from JWT-authed endpoints in a new tab.
//
// The label.pdf and reprint-label endpoints require a Bearer header, so we
// can't just point a window.open or <iframe src> at them. We fetch the PDF as a
// blob, mint a blob: URL, and open that. The browser's PDF viewer takes over
// from there. Best-effort revoke after 60s; if the user keeps the tab open
// past that, the blob is GC'd when the tab closes anyway.
//
// Auth + token freshness: this routes through apiFetchAuthed (with
// skipJsonParse: true) so we inherit its proactive-refresh + 401-retry path.
// Without that, the "idle in receive drawer for 14 min, then click Submit"
// scenario could 401 the label preview while the receive POST itself
// succeeded — the worst kind of silent failure.

import { apiFetchAuthed } from '../apiHelpers';

interface OpenAuthedPdfOptions {
  // 'GET' for label.pdf endpoints, 'POST' for /reprint-label.
  method?: 'GET' | 'POST';
  // /reprint-label returns JSON ({ label_pdf_url }) instead of a PDF —
  // when true, we recurse into the URL it points to.
  followJsonRedirect?: boolean;
}

export async function openAuthedPdf(
  path: string,
  options: OpenAuthedPdfOptions = {}
): Promise<void> {
  const { method = 'GET', followJsonRedirect = false } = options;
  // apiFetchAuthed strips the /api prefix internally via config.API_ENDPOINT,
  // and accepts either '/relative' or 'http://full' URLs.
  const url = path.startsWith('/api/') ? path.slice(4) : path;

  // skipJsonParse: true returns the raw Response object instead of parsing as
  // JSON, which is what we want for binary PDF bodies.
  const resp = (await apiFetchAuthed(url, {
    method,
    skipJsonParse: true,
    silent: true, // we surface PDF errors via the caller's toast
  })) as Response;

  if (!resp.ok) {
    throw new Error(`PDF fetch failed: ${resp.status} ${resp.statusText}`);
  }
  const contentType = resp.headers.get('content-type') || '';
  if (followJsonRedirect && contentType.includes('application/json')) {
    const json = await resp.json();
    if (json?.label_pdf_url) {
      await openAuthedPdf(json.label_pdf_url, { method: 'GET' });
      return;
    }
    throw new Error('Expected PDF, got JSON without label_pdf_url');
  }
  const blob = await resp.blob();
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
